import {createFeatureSelector, createSelector, MemoizedSelector} from '@ngrx/store';
import {AutoOrderingState} from './auto-ordering.reducer';
import {
  selectMessagesFromAppAutoOrderUpdateAsOrders,
  selectSuppliersByUserSelectedStore,
} from '../../core/store-shared/shared.selectors';
import {IStore} from '../../../shared/shared-models/store/store';
import {StockItem, StockItemUpdateFieldValue} from '../../../shared-utilities/models-old/datastructures';
import {selectSelectedStore} from '../../user-access/user-stores-store/user-store.selectors';
import {
  IAutoOrderDisableItemOutcome,
  IAutoOrderOrder,
  IAutoOrderPrepareOutcome,
  IAutoOrderSupplierEmails,
  IOpenAutoGeneratedOrder,
} from '../models';
import {ISupplier} from '../../../shared/shared-models/stock/suppliers';
import {IAutoOrderOrderingSettings} from '../../settings-new/models';
import {AOScheduleElement} from '../models/old-models/ao-datastructures';
import {decodeOnHoldCode} from '../../../shared/shared-module-ui/utils/on-hold-code/on-hold-code.utils';

export const selectAutoOrderingState = createFeatureSelector<AutoOrderingState>('auto-ordering');

export const selectIsCreatingOrderStockTake = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState): boolean => state.isCreatingOrderStockTake,
);

export const selectCreatedOrderStockTakeId = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState): string | null => state.createdOrderStockTakeId,
);

//#region SELECT SELECTED AUTO GENERATED ORDERS

/**
 * Selector to get the list of selected auto-generated order IDs.
 *
 * @param state - Auto ordering state.
 * @returns {string[]} Array of selected order IDs.
 */
export const selectSelectedAutoGeneratedOrderIds = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState): string[] => state.selectedAutoOrderIds,
);

export const selectPendingOpenAutoOrder = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState): AutoOrderingState['pendingOpenAutoOrder'] =>
    state.pendingOpenAutoOrder,
);

//#endregion

//#region SELECT OPENED AUTO GENERATED ORDERS

/**
 * Selector to get all currently open auto-generated orders.
 *
 * @param state - Auto ordering state.
 * @returns {{ [orderId: string]: IOpenAutoGeneratedOrder }} Object mapping order IDs to open orders.
 */
export const selectOpenAutoGeneratedOrders = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState): { [orderId: string]: IOpenAutoGeneratedOrder } => state.openOrder,
);

/**
 * Selector to get the open auto-generated order based on the first selected order ID.
 *
 * @param orders - Open auto-generated orders.
 * @param firstSelectedId - First selected auto-generated order ID.
 * @returns {IOpenAutoGeneratedOrder | undefined} The open order object if found.
 */
export const selectOpenAutoGeneratedOrderById = createSelector(
  selectOpenAutoGeneratedOrders,
  selectSelectedAutoGeneratedOrderIds,
  (
    orders: { [key: string]: IOpenAutoGeneratedOrder },
    [firstSelectedId]: string[],
  ): IOpenAutoGeneratedOrder | undefined => {
    let order: IOpenAutoGeneratedOrder | undefined = undefined;
    if (orders?.[firstSelectedId]) {
      order = orders?.[firstSelectedId];
    }
    return order;
  },
);

/**
 * Selector to get the loading state for adding items to the open auto order.
 *
 * @function
 * @returns {boolean} `true` if items are currently being added to the open order, otherwise `false`.
 *
 * @example
 * this.store.select(selectIsOpenOrderAddItemsLoading).subscribe(isLoading => {
 *   if (isLoading) {
 *     // show spinner
 *   }
 * });
 */
export const selectIsOpenOrderAddItemsLoading = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState): boolean => state.isOpenOrderAddItemsLoading,
);

/**
 * Selector to get the loading state for removing items from the open auto order.
 *
 * @function
 * @returns {boolean} `true` if items are currently being removed from the open order, otherwise `false`.
 *
 * @example
 * this.store.select(selectIsOpenOrderRemoveItemsLoading).subscribe(isLoading => {
 *   if (isLoading) {
 *     // show spinner
 *   }
 * });
 */
export const selectIsOpenOrderRemoveItemsLoading = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState): boolean => state.isOpenOrderRemoveItemsLoading,
);

/**
 * Selector to get the loading state for setting item tags
 * in the open auto order process.
 *
 * @returns {boolean} True if the 'set item tags' operation is loading, otherwise false.
 *
 * @example
 * this.store.select(selectIsOpenOrderSetItemTagsLoading).subscribe(isLoading => {
 *   console.info('Is setting item tags loading:', isLoading);
 * });
 */
export const selectIsOpenOrderSetItemTagsLoading = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState): boolean => state.isOpenOrderSetItemTagsLoading,
);

/**
 * Selector to get the ids of the orders with a Prepare write in flight.
 *
 * @function
 * @returns {string[]} The order ids currently being prepared.
 *
 * @example
 * this.store.select(selectPreparingAutoOrderIds).subscribe(orderIds => {
 *   if (orderIds.includes(myOrderId)) {
 *     // keep the Prepare button disabled for this order
 *   }
 * });
 */
export const selectPreparingAutoOrderIds = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState): string[] => state.preparingOrderIds,
);

/**
 * Selector to get the result of the last Prepare write for an open auto order.
 *
 * @function
 * @returns {IAutoOrderPrepareOutcome | null} The outcome, or `null` when no prepare has completed since the last clear.
 *
 * @example
 * this.store.select(selectPrepareAutoOrderOutcome).subscribe(outcome => {
 *   if (outcome?.succeeded) {
 *     // safe to clear the local draft
 *   }
 * });
 */
export const selectPrepareAutoOrderOutcome = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState): IAutoOrderPrepareOutcome | null => state.prepareOrderOutcome,
);

/**
 * Selector to determine if any CRUD operation (add/remove) on the open auto order is in progress.
 *
 * @function
 * @returns {boolean} `true` if adding and removing items is in progress, otherwise `false`.
 *
 * @example
 * this.store.select(selectIsOpenOrderCRUDLoading).subscribe(isLoading => {
 *   if (isLoading) {
 *     // disable form actions
 *   }
 * });
 */
export const selectIsOpenOrderCRUDLoading = createSelector(
  selectIsOpenOrderAddItemsLoading,
  selectIsOpenOrderRemoveItemsLoading,
  selectIsOpenOrderSetItemTagsLoading,
  (isAddLoading: boolean, isRemoveLoading: boolean, isTagsLoading: boolean): boolean =>
    isAddLoading || isRemoveLoading || isTagsLoading,
);

/**
 * Selector for whether a stock item on-hold update is in progress.
 */
export const selectIsUpdatingStockItemOnHold = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState): boolean => state.isUpdatingStockItemOnHold,
);

/**
 * Selector for whether the latest stock item on-hold update failed.
 */
/**
 * Whether a disable / enable stock update for an order item is currently being written.
 */
export const selectIsUpdatingStockItemDisabled = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState): boolean => state.isUpdatingStockItemDisabled,
);

/**
 * The values each item held before it was disabled in this session, so enabling it again
 * restores those rather than the values the disable rules wrote.
 */
export const selectStockItemDisableBaselines = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState): Record<string, Record<string, StockItemUpdateFieldValue>> => state.stockItemDisableBaselines,
);

/**
 * The item whose disabled state was last written successfully, so the open order can refresh it.
 */
export const selectStockItemDisabledOutcome = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState): IAutoOrderDisableItemOutcome | null => state.stockItemDisabledOutcome,
);

export const selectStockItemOnHoldUpdateFailed = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState): boolean => state.stockItemOnHoldUpdateFailed,
);

//#endregion

//#region SELECT AUTO GENERATED ORDERS

/**
 * Selector to get all auto-generated orders by store.
 *
 * @param state - Auto ordering state.
 * @returns {{ [storeId: string]: IAutoOrderOrder[] }} Object mapping store IDs to their auto-generated orders.
 */
export const selectAutoGeneratedOrders = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState): { [orderId: string]: IAutoOrderOrder[] } => {
    return state.autoOrderOrders;
  },
);

/**
 * Selector to get auto-generated orders for the currently selected store.
 *
 * @param orders - All auto-generated orders.
 * @param store - The currently selected store.
 * @returns {IAutoOrderOrder[]} Array of orders for the selected store.
 */
export const selectAutoGeneratedOrdersForSelectedStore = createSelector(
  selectAutoGeneratedOrders,
  selectSelectedStore,
  (orders: { [storeId: string]: IAutoOrderOrder[] }, store: IStore): IAutoOrderOrder[] => {
    if (!store?.storeId) {
      return <IAutoOrderOrder[]>[];
    }
    return orders?.[store.storeId] ?? <IAutoOrderOrder[]>[];
  },
);

/**
 * Selector to get the auto-generated order for the currently selected order ID.
 *
 * @param orders - Orders for the selected store.
 * @param orderIds - Selected auto-generated order IDs.
 * @returns {IAutoOrderOrder} The matching auto order, or an object with `orderId: 'Empty Order'` if none found.
 */
export const selectAutoGeneratedOrdersForSelectedOrder = createSelector(
  selectAutoGeneratedOrdersForSelectedStore,
  selectSelectedAutoGeneratedOrderIds,
  (orders: IAutoOrderOrder[], orderIds: string[]): IAutoOrderOrder | undefined => {
    if (!orderIds || orderIds.length === 0) {
      return undefined;
    }
    return orders.find((autoOrder: IAutoOrderOrder) => autoOrder?.orderId?.trim() === orderIds[0]?.trim());
  },
);

//#endregion

// ====================================================================================================
// Get Auto Order Orders
// ====================================================================================================
export const selectAutoOrderListSorting = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState) => {
    return {
      sortBy: state.orderListSortBy,
    };
  },
);

export const selectIsAutoOrderOrdersLoading = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState) => state.isAutoOrderOrdersLoading,
);

export const selectPendingRetryAutoOrderId = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState) => state.pendingRetryAutoOrderId,
);

export const selectIsAutoOrderDoNotAskAgainActive = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState) => state.isDeleteAutoOrderDoNotAskAgainActive,
);


export const selectAutoOrderOrdersAdditionalInfo = createSelector(
  selectAutoGeneratedOrdersForSelectedStore,
  selectSuppliersByUserSelectedStore,
  selectMessagesFromAppAutoOrderUpdateAsOrders,
  (
    stateOrders: IAutoOrderOrder[],
    suppliers: { [supplierId: string]: ISupplier },
    messageOrders: IAutoOrderOrder[],
  ): IAutoOrderOrder[] => {
    const combinedOrders = [...stateOrders, ...messageOrders];

    if (combinedOrders.length === 0) {
      return combinedOrders;
    }

    const hasSuppliers = Object.keys(suppliers).length > 0;

    return combinedOrders.map((order: IAutoOrderOrder): IAutoOrderOrder => {
      const supplierId: string = order.supplierId;
      const supplier = hasSuppliers ? suppliers[supplierId] : undefined;
      const orderTotal = order.preparedInfo?.total ? order.preparedInfo.total : undefined;
      const createdSequence = order.createdSequence === -1 ? -1 : 0;

      if (supplierId != null && supplier?.name) {
        const linkedSupplier = supplier.linkAcc ? suppliers[supplier.linkAcc] : undefined;
        return {
          ...order,
          createdSequence,
          supplierName: supplier.name,
          linkedToSupplierId: supplier.linkAcc,
          linkedToSupplierName: linkedSupplier?.name ?? '',
          onHold: decodeOnHoldCode(supplier.onHoldCode, {returnCodes: false, isSupplier: true}),
          orderTotal,
          supplierMinOrder: supplier.minOrder,
        };
      } else {
        return {
          ...order,
          createdSequence,
          supplierName: '',
          linkedToSupplierId: undefined,
          linkedToSupplierName: '',
          onHold: undefined,
          orderTotal,
          supplierMinOrder: 0,
        };
      }
    });
  },
);

export const selectSelectedAutoOrderItems = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState) => state.selectedAutoOrderItems,
);

export const selectIsAutoOrderOrderItemsLoading = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState) => state.isAutoOrderOrderItemsLoading,
);

export const selectAutoOrderingOpenOrderFilters = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState) => state.autoOrderingOrderFilters,
);


// ====================================================================================================
// Get Auto Order Stock Items
// ====================================================================================================
export const selectAutoOrderStockItemStreamCount = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState) => state.autoOrderStockItemsStreamCount,
);

export const selectIsSelectedAutoOrderStockItemsLoading = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState) => state.isAutoOrderOrderStockItemsLoading,
);

export const selectAutoOrderStockItemsState = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState) => {
    return state.autoOrderStockItems;
  },
);

export const selectSelectedAutoOrderStockItems = createSelector(
  selectAutoOrderStockItemsState,
  selectSelectedStore,
  (autoOrderStockItems, store: IStore) => {
    return autoOrderStockItems[store.storeId];
  },
);

export const selectStockItemsBySupplierIdByStreams = createSelector(
  selectSelectedAutoOrderStockItems,
  selectSelectedAutoGeneratedOrderIds,
  (streams: { [p: string]: { [p: string]: StockItem[] } }, orderIds: string[]) => {
    if (!streams || !orderIds || orderIds.length !== 1) {
      return {};
    }
    return streams[orderIds[0]] || {};
  },
);

export const selectStockItemsCombinedQueries = createSelector(
  selectStockItemsBySupplierIdByStreams,
  (queries: { [p: string]: StockItem[] }) => {
    if (queries['mainSupplierStockData']?.length > -1 && queries['additionalSupplierStockData']?.length > -1) {
      return Object.keys(queries).reduce((combined: StockItem[], streamKey: string) => {
        const stockItems = queries[streamKey];
        return combined.concat(stockItems);
      }, []);
    } else {
      return [] as StockItem[];
    }
  },
);

// ====================================================================================================
// Stock Item Deletion Rules
// ====================================================================================================
export const selectIsAutoOrderStockDeletionLoading = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState) => state.isAutoOrderStockDeletionRulesLoading,
);

export const selectAutoOrderStockDeletionRules = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState) => state.disabledRules,
);

// ====================================================================================================
// Recipients and Suppliers
// ====================================================================================================
//#region SELECT SPECIFIED SUPPLIER EMAILS

export const selectIsSupplierEmailsLoading = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState) => state.isSupplierEmailsLoading,
);

export const selectSupplierEmails = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState) => <{ [p: string]: { [p: string]: IAutoOrderSupplierEmails } }>state.supplierEmails,
);

export const selectSpecifiedSupplierEmails = createSelector(
  selectSupplierEmails,
  selectSelectedStore,
  (
    emails: Record<string, Record<string, IAutoOrderSupplierEmails>>,
    store: IStore,
  ): { [supplierId: string]: IAutoOrderSupplierEmails } => {
    return emails?.[store?.storeId] ?? {};
  },
);

// ====================================================================================================
// Order Settings
// ====================================================================================================
export const selectIsAutoOrderOrderSettingsLoading = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState) => state.isAutoOrderOrderSettingsLoading,
);

export const selectAutoOrderOrderSettings = createSelector(
  selectAutoOrderingState,
  selectSelectedStore,
  (state: AutoOrderingState, store: IStore) => state.autoOrderOrderSettings[store.storeId] || {} as IAutoOrderOrderingSettings,
);

// ====================================================================================================
// Schedule Auto Delete Settings
// ====================================================================================================
export const selectAutoOrderScheduleDeleteSettings = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState) => state.scheduleDeleteSettings,
);
export const selectAddStockItemTypesenseData = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState) => state.addStockItemsData,
);
export const selectAddItemFilter = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState) => state.addStockItemsData.filter,
);

export const selectAddItemLineColours = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState) => state.addStockItemsData.lineColour,
);

export const selectAddItemFacetCounts = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState) => state.addStockItemsData.facetCounts,
);

export const selectAutoOrderStatusChips = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState) => state.autoOrderStatusChips,
);

// ====================================================================================================
// Schedule Settings
// ====================================================================================================
export const selectIsAutoOrderScheduleLoading = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState) => state.isAutoOrderScheduleLoading,
);

export const selectAutoOrderScheduleData = createSelector(
  selectAutoOrderingState,
  selectSelectedStore,
  (state: AutoOrderingState, store: IStore) => {
    if (!store?.storeId) {
      return {};
    }
    return state.autoOrderScheduleData[store.storeId] || {};
  },
);

/**
 * Cache for day-specific selectors to ensure proper memoization.
 * Without caching, each call to selectAutoOrderScheduleDataForDay would create
 * a new selector instance, breaking memoization.
 */
const daySelectorsCache = new Map<string, MemoizedSelector<object, Record<string, AOScheduleElement>>>();

/**
 * Returns a memoized selector for schedule data for a specific day.
 * Selectors are cached per day to maintain memoization across multiple calls.
 *
 * @param day - The day of the week (e.g., 'Monday', 'Tuesday')
 * @returns Memoized selector for that day's schedule data
 */
export function selectAutoOrderScheduleDataForDay(
  day: string,
): MemoizedSelector<object, Record<string, AOScheduleElement>> {
  if (!daySelectorsCache.has(day)) {
    const selector = createSelector(
      selectAutoOrderScheduleData,
      (scheduleData: Record<string, Record<string, AOScheduleElement>>) => {
        return scheduleData[day] || {};
      },
    );
    daySelectorsCache.set(day, selector);
  }

  return daySelectorsCache.get(day);
}

export const selectLastScheduleUpdateTimestamp = createSelector(
  selectAutoOrderingState,
  (state: AutoOrderingState) => state.lastScheduleUpdateTimestamp,
);

/**
 * Selects the schedule loading state for the currently selected store.
 *
 * @returns Loading state object containing:
 * - isLoading: boolean - Whether schedule is currently loading
 * - daysLoaded: string[] - Array of day keys that have loaded
 * - suppliersLoaded: boolean - Whether suppliers have loaded
 * - expectedDayCount: number - Expected number of days
 * - error: IGlobalError | null - Any error that occurred
 */
export const selectScheduleLoadingState = createSelector(
  selectAutoOrderingState,
  selectSelectedStore,
  (state: AutoOrderingState, store: IStore) => {
    if (!store?.storeId) {
      return {
        isLoading: false,
        daysLoaded: [],
        suppliersLoaded: false,
        expectedDayCount: 0,
        error: null,
      };
    }
    return state.scheduleLoadingState[store.storeId] || {
      isLoading: false,
      daysLoaded: [],
      suppliersLoaded: false,
      expectedDayCount: 0,
      error: null,
    };
  },
);

/**
 * Computed selector that provides loading progress information for the schedule.
 *
 * @returns Loading progress object containing:
 * - isLoading: boolean - Whether schedule is currently loading
 * - progress: number - Loading progress as percentage (0-100)
 * - error: IGlobalError | null - Any error that occurred
 * - daysLoaded: number - Number of days that have loaded
 * - totalDays: number - Total expected days
 */
export const selectScheduleLoadingProgress = createSelector(
  selectScheduleLoadingState,
  (loadingState) => {
    const {isLoading, daysLoaded, expectedDayCount, suppliersLoaded, error} = loadingState;

    // Calculate progress: 70% for days, 30% for suppliers
    const daysProgress = expectedDayCount > 0
      ? (daysLoaded.length / expectedDayCount) * 0.7
      : 0;
    const suppliersProgress = suppliersLoaded ? 0.3 : 0;
    const totalProgress = Math.round((daysProgress + suppliersProgress) * 100);

    return {
      isLoading,
      progress: totalProgress,
      error,
      daysLoaded: daysLoaded.length,
      totalDays: expectedDayCount,
    };
  },
);
